import { Badge, Box, IconButton, Stack, SvgIconProps, useTheme } from '@mui/material'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { useInstallationForm, useInstallationIsGerman } from 'src/Libs/flow/installation/hooks'
import { getAvailableIo, getIsHeatpumpAvailable, hasAvailableIo } from 'src/Libs/flow/installation/utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { AddIcon } from '../../../icons/Icons'
import { BatteryIcon, ElectricCarIcon, EmcIcon, HeatpumpCenteredIcon, IoInterfaceIcon, MeterIcon } from '../../../icons/SettingsIcons'
import { MAXIMUM_EVSES_PER_INSTALLATION, MAXIMUM_METERS_PER_INSTALLATION } from '../../constants'
import { useAddDeviceDialog } from '../../hooks/use-add-device-dialog'
import { AddDeviceTab } from '../Tabs'
import { useIsFarmFeatureAvailableForFlowInstallation } from 'src/Hooks'

const iconSize = 50
const emcIconSize = 64

const linesHeightPx = 48

export const SummaryGraphic: React.FC = () => {
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control } = useInstallationForm()
  const emc = useWatch({ control, name: 'emcDevice' })
  const storages = useWatch({ control, name: 'storageDevices' })
  const modbusTcpChargingStations = useWatch({ control, name: 'chargingStations.modbusTcp' })
  const eebusChargingStations = useWatch({ control, name: 'chargingStations.eebus' })
  const heatpump = useWatch({ control, name: 'heatpump' })
  const meters = useWatch({ control, name: 'meters' })
  const io = useWatch({ control, name: 'ioInterfaces' })
  const isFarmFeatureAvailableForFlowInstallation = useIsFarmFeatureAvailableForFlowInstallation()

  const isGerman = useInstallationIsGerman()

  const chargingStationsAmount = modbusTcpChargingStations.length + eebusChargingStations.length

  const ioCount: number = Number(Boolean(io.htc320h)) + Number(Boolean(io.storage)) + Number(Boolean(io.mypv))

  const openAddDeviceDialog = useAddDeviceDialog()

  if (emc === null) {
    return (
      <Stack alignItems={'center'}>
        <EmcIcon color="disabled" sx={{ fontSize: iconSize }} />
        <IconButton size="small" onClick={() => openAddDeviceDialog('EMC')} sx={{ visibility: canEdit ? undefined : 'hidden' }}>
          <AddIcon />
        </IconButton>
      </Stack>
    )
  }

  return (
    <>
      <Box display={'flex'} justifyContent={'center'}>
        <EmcIcon
          sx={{
            fontSize: emcIconSize,
            translate: '0 6px',
            color: theme => theme.palette.primary.dark,
          }}
        />
      </Box>
      <Box mx={`${iconSize / 2}px`} height={linesHeightPx}>
        <SummaryGraphicLines
          areWallboxesActive={!!chargingStationsAmount}
          isBatteryActive={storages.length > 0}
          isHeatpumpActive={!!heatpump}
          areMetersActive={!!meters.length}
          areIOInterfacesActive={!!ioCount}
        />
      </Box>

      <Stack direction={'row'} justifyContent={'space-between'}>
        <SummaryGraphicItem
          Icon={ElectricCarIcon}
          addDeviceDialogTab="Charging Station"
          count={chargingStationsAmount}
          disabled={chargingStationsAmount >= MAXIMUM_EVSES_PER_INSTALLATION}
        />
        <SummaryGraphicItem
          Icon={BatteryIcon}
          addDeviceDialogTab="Storage System"
          count={storages.length}
          disabled={!isFarmFeatureAvailableForFlowInstallation && storages.length > 0}
        />
        <SummaryGraphicItem
          Icon={HeatpumpCenteredIcon}
          addDeviceDialogTab="Hvac"
          count={heatpump ? 1 : 0}
          disabled={!getIsHeatpumpAvailable(io, heatpump, isGerman)}
        />
        <SummaryGraphicItem
          Icon={MeterIcon}
          addDeviceDialogTab="Sub Meter"
          count={meters.length}
          disabled={meters.length >= MAXIMUM_METERS_PER_INSTALLATION}
        />
        <SummaryGraphicItem
          Icon={IoInterfaceIcon}
          addDeviceDialogTab="I/O Interface"
          count={ioCount}
          disabled={!hasAvailableIo(getAvailableIo(io, heatpump, isGerman))}
        />
      </Stack>
    </>
  )
}

/**
 * @returns A function that returns the active line color if at least one of the conditions is true.
 * Otherwise, it returns the inactive line color.
 */
const useLineColor = (): ((...activeConditions: boolean[]) => string) => {
  const theme = useTheme()
  const inactiveLineColor = theme.palette.grey.grey06
  const activeLineColor = theme.palette.blue.sky

  const getLineColor = (...activeConditions: boolean[]): string => {
    return activeConditions.some(condition => condition) ? activeLineColor : inactiveLineColor
  }

  return getLineColor
}

interface SummaryGraphicLinesProps {
  areWallboxesActive: boolean
  isBatteryActive: boolean
  isHeatpumpActive: boolean
  areMetersActive: boolean
  areIOInterfacesActive: boolean
}

const SummaryGraphicLines: React.FC<SummaryGraphicLinesProps> = ({
  areWallboxesActive,
  isBatteryActive,
  isHeatpumpActive,
  areMetersActive,
  areIOInterfacesActive,
}) => {
  const getLineColor = useLineColor()

  return (
    <Box
      component={'svg'}
      width="100%"
      height={48}
      viewBox="0 0 48 48"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      strokeWidth={'3'}
      strokeLinecap="square"
      overflow={'visible'}
      shapeRendering="crispEdges"
      sx={{
        '& line': {
          vectorEffect: 'non-scaling-stroke',
        },
      }}
    >
      <line x1="0%" x2="0%" y1="28px" y2="100%" stroke={getLineColor(areWallboxesActive)} />
      <line x1="25%" x2="25%" y1="28px" y2="100%" stroke={getLineColor(isBatteryActive)} />
      <line x1="50%" x2="50%" y1="28px" y2="100%" stroke={getLineColor(isHeatpumpActive)} />
      <line x1="75%" x2="75%" y1="28px" y2="100%" stroke={getLineColor(areMetersActive)} />
      <line x1="100%" x2="100%" y1="28px" y2="100%" stroke={getLineColor(areIOInterfacesActive)} />

      <line x1="0%" x2="25%" y1="28px" y2="28px" stroke={getLineColor(areWallboxesActive)} />
      <line x1="25%" x2="50%" y1="28px" y2="28px" stroke={getLineColor(areWallboxesActive, isBatteryActive)} />
      <line x1="75%" x2="100%" y1="28px" y2="28px" stroke={getLineColor(areIOInterfacesActive)} />
      <line x1="50%" x2="75%" y1="28px" y2="28px" stroke={getLineColor(areMetersActive, areIOInterfacesActive)} />

      <line
        x1="50%"
        x2="50%"
        y2="28px"
        stroke={getLineColor(areWallboxesActive, isBatteryActive, isHeatpumpActive, areMetersActive, areIOInterfacesActive)}
      />
    </Box>
  )
}

interface SummaryGraphicItemProps {
  Icon: React.FC<SvgIconProps>
  addDeviceDialogTab: AddDeviceTab
  count: number | undefined
  disabled?: boolean
}

const SummaryGraphicItem: React.FC<SummaryGraphicItemProps> = ({ Icon, addDeviceDialogTab, count, disabled }) => {
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const openAddDeviceDialog = useAddDeviceDialog()
  const handleClick = (): void => openAddDeviceDialog(addDeviceDialogTab)

  return (
    <Stack alignItems={'center'} mt={1}>
      <Badge badgeContent={count} color="primary">
        <Icon
          color="disabled"
          sx={{
            fontSize: iconSize,
            color: theme => (count ? theme.palette.primary.dark : undefined),
          }}
        />
      </Badge>
      {canEdit && (
        <IconButton color="primary" size="small" onClick={handleClick} disabled={disabled}>
          <AddIcon />
        </IconButton>
      )}
    </Stack>
  )
}
